import React from 'react';
import { Box, Typography, Container, useMediaQuery, useTheme as useMuiTheme } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useTheme } from '../context/ThemeContext';
import { getCategoryById, MOST_LIKED_STORY_IDS } from '../config/categories';
import { getPublishedStories } from '../data';
import SEO from '../components/SEO';
import Header from '../components/Header';

const Category: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  
  const category = getCategoryById(categoryId || '');
  const allStories = getPublishedStories();
  
  // Filter stories that belong to this category
  // Special handling for "most-liked" category using static list
  const categoryStories = categoryId === 'most-liked'
    ? MOST_LIKED_STORY_IDS
        .map(id => allStories.find(story => story.id === id))
        .filter((story): story is NonNullable<typeof story> => story !== undefined)
    : allStories.filter(story => story.categories.includes(categoryId || ''));

  const colors = {
    bg: isDark ? '#0a0a0a' : '#fafafa',
    cardBg: isDark ? '#141414' : '#ffffff',
    text: isDark ? '#f5f5f5' : '#0a0a0a',
    textSecondary: isDark ? '#a0a0a0' : '#555',
    textMuted: isDark ? '#666' : '#999',
    border: isDark ? '#1f1f1f' : '#eee',
    hover: isDark ? '#1a1a1a' : '#f5f5f5',
    accent: isDark ? category?.color.textDark : category?.color.text,
  };

  if (!category) {
    return (
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: colors.bg
      }}>
        <Header isMobile={isMobile} />
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Typography variant="h4" sx={{ color: colors.text }}>
            Category not found
          </Typography>
        </Box>
      </Box>
    );
  }

  // Generate breadcrumb structured data
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://dailystories.fr"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": category.name,
        "item": `https://dailystories.fr/category/${category.id}`
      }
    ]
  };

  // Generate collection structured data
  const collectionStructuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${category.name} Stories`,
    "description": category.description,
    "url": `https://dailystories.fr/category/${category.id}`,
    "publisher": {
      "@type": "Organization",
      "name": "Daily Stories",
      "url": "https://dailystories.fr"
    },
    "numberOfItems": categoryStories.length,
    "itemListElement": categoryStories.slice(0, 10).map((story, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Article",
        "headline": story.title,
        "description": story.excerpt,
        "url": `https://dailystories.fr/story/${story.id}`,
        "datePublished": story.publishDate,
        "author": {
          "@type": "Person",
          "name": "Marin"
        }
      }
    }))
  };

  return (
    <>
      <SEO 
        title={`${category.name} Erotic Stories | Daily Stories - ${categoryStories.length} Explicit ${category.name} Tales`}
        description={`${categoryStories.length} intense ${category.name.toLowerCase()} erotic stories. ${category.description}. Explicit adult fiction exploring forbidden desires & fetish fantasies. 18+ only.`}
        keywords={`${category.name.toLowerCase()} erotic stories, explicit ${category.name.toLowerCase()} fiction, ${category.description.toLowerCase()}, intense adult stories, fetish ${category.name.toLowerCase()}, taboo stories, hardcore erotica, 18+ fiction`}
        url={`https://dailystories.fr/category/${category.id}`}
      />
      
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionStructuredData) }}
      />
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
      />
      
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: colors.bg
      }}>
        {/* Header */}
        <Header isMobile={isMobile} />

        {/* Category Content */}
        <Box sx={{ flex: 1, pb: 6 }}>
          {/* Compact Header */}
          <Box 
            component="header"
            sx={{
              py: { xs: 3, md: 4 },
              px: { xs: 2, md: 4 },
              borderBottom: `1px solid ${colors.border}`,
              background: isDark 
                ? `linear-gradient(180deg, ${category.color.bgDark} 0%, transparent 100%)`
                : `linear-gradient(180deg, ${category.color.bg} 0%, transparent 100%)`,
            }}
          >
            <Container maxWidth="lg">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ fontSize: '2rem' }}>{category.emoji}</Typography>
                <Box>
                  <Typography
                    variant="h1"
                    component="h1"
                    sx={{ 
                      color: colors.text, 
                      fontSize: { xs: '1.5rem', md: '1.75rem' },
                      fontWeight: 600,
                      lineHeight: 1.2
                    }}
                  >
                    {category.name}
                  </Typography>
                  <Typography
                    sx={{ 
                      color: colors.textSecondary, 
                      fontSize: '0.9rem',
                      mt: 0.5
                    }}
                  >
                    {category.description} · {categoryStories.length} stories
                  </Typography>
                </Box>
              </Box>
            </Container>
          </Box>

          <Container maxWidth="lg" sx={{ mt: 3 }}>
            {categoryStories.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography sx={{ color: colors.textSecondary, fontSize: '0.95rem' }}>
                  No stories yet. Check back soon!
                </Typography>
              </Box>
            ) : (
              <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 2 
            }}>
                {categoryStories.map((story, index) => (
                  <Box key={story.id}>
                    <Box
                      component="article"
                      onClick={() => navigate(`/story/${story.id}`)}
                      sx={{
                        backgroundColor: colors.cardBg,
                        borderRadius: '12px',
                        border: `1px solid ${colors.border}`,
                        p: { xs: 2.5, md: 3 },
                        cursor: 'pointer',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: isDark 
                            ? `0 20px 40px -12px rgba(0,0,0,0.5)`
                            : `0 20px 40px -12px rgba(0,0,0,0.1)`,
                          borderColor: colors.accent,
                        }
                      }}
                    >
                      {/* Header Row */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography
                          component="time"
                          dateTime={story.publishDate}
                          sx={{ 
                            color: colors.textMuted,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          {format(new Date(story.publishDate), 'MMM d')}
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 0.25, 
                          alignItems: 'center',
                          backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                          borderRadius: '8px',
                          px: 1,
                          py: 0.5
                        }}>
                          {[1, 2, 3].map((level) => (
                            <Box
                              key={level}
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: level <= story.intensity 
                                  ? colors.accent 
                                  : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'),
                                transition: 'all 0.2s ease'
                              }}
                            />
                          ))}
                        </Box>
                      </Box>

                      {/* Title */}
                      <Typography
                        variant="h2"
                        component="h2"
                        sx={{
                          color: colors.text,
                          mb: 1,
                          fontSize: { xs: '1rem', md: '1.1rem' },
                          fontWeight: 600,
                          lineHeight: 1.3
                        }}
                      >
                        {story.title}
                      </Typography>

                      {/* Excerpt */}
                      <Typography
                        sx={{
                          color: colors.textSecondary,
                          mb: 2,
                          fontSize: '0.85rem',
                          lineHeight: 1.6,
                          flex: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {story.excerpt}
                      </Typography>
                      
                      {/* Footer */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pt: 1.5,
                        mt: 'auto',
                        borderTop: `1px solid ${colors.border}`
                      }}>
                        <Typography sx={{ color: colors.accent, fontSize: '0.75rem', fontWeight: 500 }}>
                          {story.city}
                        </Typography>
                        <Typography sx={{ color: colors.textMuted, fontSize: '0.7rem' }}>
                          {story.readTime}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default Category;
